import React from 'react'
import { config, Container } from 'react-awesome-styled-grid'
import Media from 'react-media'
import { useQuery } from 'react-query'
import { get, isEmpty } from 'lodash'
import PropTypes from 'prop-types'
import styled from 'styled-components'

import { fetchPostsByCategory } from '~/api'
import FeaturedItems from '~/components/FeaturedItems'
import InfinitePosts from '~/components/InfinitePosts'
import Layout from '~/components/Layout'
import NotFoundError from '~/components/NotFoundError'
import ResultsHead from '~/components/ResultsHead'
import { ResultPageTypes } from '~/helpers/constants'
import { isMobileDeviceType, slicePosts } from '~/helpers/utils'

/**
 * Constants
 */

const QUERY_KEY = 'category'

/**
 * getServerSideProps
 */

export async function getServerSideProps({ query, res, req }) {
  res.setHeader('Cache-Control', 's-maxage=300, stale-while-revalidate')

  const { category } = query
  const posts = await fetchPostsByCategory(QUERY_KEY, category)

  const isMobile = isMobileDeviceType(req)

  return { props: { posts, category, isMobile } }
}

/**
 * CategoryPage
 */

const CategoryPage = ({ posts, category, isMobile }) => {
  const { data } = useQuery([QUERY_KEY, category], fetchPostsByCategory, {
    initialData: posts,
  })
  const { featuredPosts, recentPosts } = slicePosts(data)

  const firstImage = get(data, '[0].images.original')

  const shouldShowDate = category !== 'noticias'

  const parsedCategory = category.replace('-', ' ')

  return (
    <Media
      queries={{
        small: '(max-width: 768px)',
      }}
      defaultMatches={{ small: isMobile }}
    >
      {matches => (
        <Layout isMobile={isMobile}>
          {isEmpty(posts) && <NotFoundError />}
          {!isEmpty(featuredPosts) && (
            <>
              <ResultsHead name={parsedCategory} type={ResultPageTypes.category} image={firstImage} />
              <HeroWrapper>
                <SectionHeader>
                  <Container>{parsedCategory}</Container>
                </SectionHeader>
                {!matches.small && (
                  <Container>
                    <FeaturedItems showDate={shouldShowDate} showCategory={false} data={featuredPosts} />
                  </Container>
                )}
                {matches.small && <FeaturedItems showDate={shouldShowDate} showCategory={false} data={featuredPosts} />}
              </HeroWrapper>
            </>
          )}
          {!isEmpty(recentPosts) && (
            <InfinitePosts
              initialRecentPosts={recentPosts}
              fetch={fetchPostsByCategory}
              queryKey={category}
              showCategory={false}
            />
          )}
        </Layout>
      )}
    </Media>
  )
}

/**
 * PropTypes
 */

CategoryPage.propTypes = {
  posts: PropTypes.array,
  category: PropTypes.string,
  isMobile: PropTypes.bool.isRequired,
}

/**
 * Styles
 */

const SectionHeader = styled.h1`
  margin: 0;
  padding: 0.6em 0.2em;
  background: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.textOverImage};
  font-weight: 900;
  text-transform: uppercase;
  box-shadow: 2px 0px 1px ${props => props.theme.colors.primaryShadow};

  ${props => config(props).media.md`
    margin-bottom: 10px;
    padding: 1em 0;
  `}
`

const HeroWrapper = styled.div`
  background-color: ${props => props.theme.colors.feature};
  padding: 0;
  margin-bottom: 30px;

  ${props => config(props).media.lg`
    padding: 0 0 30px 0;
  `}
`

/**
 * Exports
 */

export default CategoryPage
